import { getAuth, User } from 'firebase/auth';

/**
 * Fired on `window` after the RoleSwitcher auto-aligns the server's active
 * context (family -> provider), so pages that fetched memberships earlier
 * (MyRolesPage) can refetch instead of showing a stale active role (MP-6831).
 */
export const ROLE_CONTEXT_CHANGED_EVENT = 'ws:role-context-changed';

/**
 * Resolves a Firebase ID token, waiting for auth to finish restoring the
 * session first. On a cold page load `getAuth().currentUser` is still null for
 * a moment, and click handlers that bail on the missing token silently drop
 * the click (MP-6830). Resolves null only when auth settles signed-out.
 */
export async function getIdTokenWhenReady(): Promise<string | null> {
  const auth = getAuth();
  const user =
    auth.currentUser ??
    (await new Promise<User | null>((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((restored) => {
        unsubscribe();
        resolve(restored);
      });
    }));

  if (!user) return null;
  return user.getIdToken();
}

const PROVIDER_MEMBERSHIP_KEY = 'ws:has-provider-membership';

/**
 * Fired on `window` whenever the provider-membership marker is written. A guard
 * holding on `unknown` listens for this so it can act the moment the answer
 * lands instead of sitting out its timeout.
 */
export const PROVIDER_MEMBERSHIP_KNOWN_EVENT = 'ws:provider-membership-known';

/**
 * `unknown` is a real answer, not a missing one: on a cold load or in a new tab
 * nobody has fetched memberships yet, and reading that as "no provider role"
 * ejected multi-role users before the switcher could answer (MP-6832).
 */
export type ProviderMembershipMarker = 'yes' | 'no' | 'unknown';

/**
 * Records whether this user holds any non-family membership. The RoleSwitcher
 * knows (it fetches memberships); RestrictedRoute needs to know but only sees
 * claims, and a user switched to their parent role has parent-only claims
 * whether or not they are also staff somewhere.
 *
 * Without this, two behaviors race on a guarded CCMS route: auto-align
 * switching the user back to their provider role, and the parent eject sending
 * them to the parent app. Whichever landed first decided the outcome, so the
 * same deep link went to /parents once and bounced off the parent app to the
 * marketing search page the next time (MP-6832).
 *
 * The marker is stamped with the uid it describes, so a second person signing in
 * on a shared browser cannot inherit the first person's answer.
 */
export function setHasProviderMembership(hasProvider: boolean): void {
  const uid = getAuth().currentUser?.uid;
  // Without a uid we cannot say whose answer this is. Leave it unknown.
  if (!uid) return;

  try {
    sessionStorage.setItem(PROVIDER_MEMBERSHIP_KEY, `${uid}:${hasProvider ? '1' : '0'}`);
  } catch {
    // Private-mode sessionStorage throws. Unknown is the safe answer: the guard
    // then holds and falls through to its timeout, as it did before.
  }
  window.dispatchEvent(new Event(PROVIDER_MEMBERSHIP_KNOWN_EVENT));
}

/** Reads the marker for `uid`. Someone else's answer, or none at all, reads as `unknown`. */
export function getProviderMembershipMarker(uid?: string | null): ProviderMembershipMarker {
  if (!uid) return 'unknown';

  try {
    const stored = sessionStorage.getItem(PROVIDER_MEMBERSHIP_KEY);
    if (stored === `${uid}:1`) return 'yes';
    if (stored === `${uid}:0`) return 'no';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

const AUTO_ALIGN_ATTEMPTED_KEY = 'ws:auto-align-attempted';

/**
 * Auto-align reloads the page, which re-mounts the switcher with a fresh
 * in-memory guard — so the guard has to outlive the reload, or a switch that
 * keeps answering "still family" reloads forever. The attempt is parked here
 * with its timestamp: a repeat seconds later is that loop and is refused, while
 * a genuine second round trip from the parent app minutes later still aligns.
 */
const AUTO_ALIGN_COOLDOWN_MS = 60_000;

export function markAutoAlignAttempted(): void {
  try {
    sessionStorage.setItem(AUTO_ALIGN_ATTEMPTED_KEY, String(Date.now()));
  } catch {
    // Private mode: the in-memory guard still stops a repeat within one mount.
  }
}

export function autoAlignAttemptedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(AUTO_ALIGN_ATTEMPTED_KEY));
    return at > 0 && Date.now() - at < AUTO_ALIGN_COOLDOWN_MS;
  } catch {
    return false;
  }
}

const PENDING_ALIGN_TOAST_KEY = 'ws:pending-align-toast';

/**
 * Auto-align reloads the page so the app re-reads the switched claims, which
 * would throw away the "we switched you back" toast. Park the role label and
 * show it once on the next boot.
 */
export function setPendingAlignToast(roleLabel: string): void {
  try {
    sessionStorage.setItem(PENDING_ALIGN_TOAST_KEY, roleLabel);
  } catch {
    // Private mode: losing the toast is acceptable, the switch still happened.
  }
}

export function takePendingAlignToast(): string | null {
  try {
    const label = sessionStorage.getItem(PENDING_ALIGN_TOAST_KEY);
    if (label) sessionStorage.removeItem(PENDING_ALIGN_TOAST_KEY);
    return label;
  } catch {
    return null;
  }
}

// Same-role entries at different schools are otherwise identical; org_name is null for family roles.
export const withSchoolName = (orgName: string | null | undefined, roleLabel: string) =>
  orgName ? `${orgName} · ${roleLabel}` : roleLabel;
